import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Select, Card, Table, Spin, Empty, Tag, Statistic, Divider, Progress, Alert } from "antd";
import {
  DashboardOutlined,
  PlayCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FundOutlined,
  UserOutlined,
  FallOutlined,
} from "@ant-design/icons";
import QoEOverviewCards from "./QoEOverviewCards";
import QoEBreakdownTable from "./QoEBreakdownTable";

const { Option } = Select;

const TIMEFRAME_OPTIONS = [
  { label: "7 dias", value: "7:days" },
  { label: "30 dias", value: "30:days" },
  { label: "90 dias", value: "90:days" },
];

const QoESection = ({
  muxError,
  qoeOverview,
  qoeByDevice,
  qoeByPlan,
  qoeByCategory,
  qoeByDifficulty,
  qoeTimeseries,
  trailQoe,
  trailEngagement,
  trailFunnel,
  moduleQoe,
  moduleDropOff,
  videoQoe,
  videoEngagement,
  topVideos,
  tracks,
  modules,
  loading,
  fetchQoEOverview,
  fetchQoEByDevice,
  fetchQoEByPlan,
  fetchQoEByCategory,
  fetchQoEByDifficulty,
  fetchQoETimeseries,
  fetchTrailQoE,
  fetchTrailEngagement,
  fetchTrailFunnel,
  fetchModuleQoE,
  fetchModuleDropOff,
  fetchVideoQoE,
  fetchVideoEngagement,
  fetchTracks,
  fetchModulesForSelector,
}) => {
  const [timeframe, setTimeframe] = useState("7:days");
  const [selectedTrailId, setSelectedTrailId] = useState(null);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [selectedVideoId, setSelectedVideoId] = useState(null);

  const loadQoEData = useCallback((tf) => {
    fetchQoEOverview(tf);
    fetchQoEByDevice(tf);
    fetchQoEByPlan(tf);
    fetchQoEByCategory(tf);
    fetchQoEByDifficulty(tf);
    fetchQoETimeseries(tf);
  }, [fetchQoEOverview, fetchQoEByDevice, fetchQoEByPlan, fetchQoEByCategory, fetchQoEByDifficulty, fetchQoETimeseries]);

  useEffect(() => {
    loadQoEData(timeframe);
    if (fetchTracks) fetchTracks({ perPage: 100 });
    if (fetchModulesForSelector) fetchModulesForSelector({ perPage: 100 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTimeframeChange = (tf) => {
    setTimeframe(tf);
    loadQoEData(tf);
    if (selectedTrailId) {
      fetchTrailQoE(selectedTrailId, tf);
      fetchTrailEngagement(selectedTrailId, tf);
    }
    if (selectedModuleId) {
      fetchModuleQoE(selectedModuleId, tf);
      fetchModuleDropOff(selectedModuleId, tf);
    }
    if (selectedVideoId) {
      fetchVideoQoE(selectedVideoId, tf);
      fetchVideoEngagement(selectedVideoId, tf);
    }
  };

  const handleTrailSelect = (trailId) => {
    setSelectedTrailId(trailId);
    if (trailId) {
      fetchTrailQoE(trailId, timeframe);
      fetchTrailEngagement(trailId, timeframe);
      fetchTrailFunnel(trailId);
    }
  };

  const handleModuleSelect = (moduleId) => {
    setSelectedModuleId(moduleId);
    if (moduleId) {
      fetchModuleQoE(moduleId, timeframe);
      fetchModuleDropOff(moduleId, timeframe);
    }
  };

  const handleVideoSelect = (videoId) => {
    setSelectedVideoId(videoId);
    if (videoId) {
      fetchVideoQoE(videoId, timeframe);
      fetchVideoEngagement(videoId, timeframe);
    }
  };

  const trackList = tracks?.data || tracks || [];
  const moduleList = modules?.data || modules || [];
  const videoList = topVideos || [];

  return (
    <div>
      {/* Timeframe selector */}
      <div style={{ marginBottom: 16 }}>
        <span style={{ marginRight: 8, fontWeight: 500 }}>Periodo:</span>
        <Select value={timeframe} onChange={handleTimeframeChange} style={{ width: 140 }}>
          {TIMEFRAME_OPTIONS.map((opt) => (
            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
          ))}
        </Select>
      </div>

      {muxError && (
        <Alert
          message="Mux Data API"
          description={muxError}
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      {/* QoE Overview Cards */}
      <div style={{ marginBottom: 24 }}>
        <QoEOverviewCards data={qoeOverview} loading={loading.qoeOverview} />
      </div>

      {/* Breakdowns Grid 2x2 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <QoEBreakdownTable title="Por Dispositivo" data={qoeByDevice} loading={loading.qoeBreakdowns} />
        </Col>
        <Col xs={24} lg={12}>
          <QoEBreakdownTable title="Por Plano" data={qoeByPlan} loading={loading.qoeBreakdowns} />
        </Col>
        <Col xs={24} lg={12}>
          <QoEBreakdownTable title="Por Categoria" data={qoeByCategory} loading={loading.qoeBreakdowns} />
        </Col>
        <Col xs={24} lg={12}>
          <QoEBreakdownTable title="Por Dificuldade" data={qoeByDifficulty} loading={loading.qoeBreakdowns} />
        </Col>
      </Row>

      {/* Timeseries */}
      <div style={{ marginBottom: 24 }}>
        <QoETimeseriesTable data={qoeTimeseries} loading={loading.qoeTimeseries} />
      </div>

      {/* Drill-down: Trail */}
      <Divider orientation="left">Drill-down: Trilha</Divider>
      <div style={{ marginBottom: 16 }}>
        <Select
          placeholder="Selecione uma trilha"
          style={{ width: 400 }}
          allowClear
          showSearch
          optionFilterProp="children"
          value={selectedTrailId}
          onChange={handleTrailSelect}
        >
          {trackList.map((t) => (
            <Option key={t._id || t.id} value={t._id || t.id}>{t.name || t.title}</Option>
          ))}
        </Select>
      </div>
      {selectedTrailId && (
        <div style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <QoEOverviewCards data={trailQoe} loading={loading.trailQoe} />
            </Col>
            <Col xs={24} lg={12}>
              <TrailEngagementCards data={trailEngagement} loading={loading.trailEngagement} />
            </Col>
          </Row>
          {trailFunnel && (
            <div style={{ marginTop: 16 }}>
              <TrailFunnelTable data={trailFunnel} loading={loading.trailFunnel} />
            </div>
          )}
        </div>
      )}

      {/* Drill-down: Module */}
      <Divider orientation="left">Drill-down: Modulo</Divider>
      <div style={{ marginBottom: 16 }}>
        <Select
          placeholder="Selecione um modulo"
          style={{ width: 400 }}
          allowClear
          showSearch
          optionFilterProp="children"
          value={selectedModuleId}
          onChange={handleModuleSelect}
        >
          {moduleList.map((m) => (
            <Option key={m._id || m.id} value={m._id || m.id}>{m.name || m.title}</Option>
          ))}
        </Select>
      </div>
      {selectedModuleId && (
        <div style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <QoEOverviewCards data={moduleQoe} loading={loading.moduleQoe} />
            </Col>
            <Col xs={24} lg={12}>
              <ModuleDropOffTable data={moduleDropOff} loading={loading.moduleDropOff} />
            </Col>
          </Row>
        </div>
      )}

      {/* Drill-down: Video */}
      <Divider orientation="left">Drill-down: Video</Divider>
      <div style={{ marginBottom: 16 }}>
        <Select
          placeholder="Selecione um video (top videos)"
          style={{ width: 400 }}
          allowClear
          showSearch
          optionFilterProp="children"
          value={selectedVideoId}
          onChange={handleVideoSelect}
        >
          {videoList.map((v) => (
            <Option key={v.videoId} value={v.videoId}>{v.videoName}</Option>
          ))}
        </Select>
      </div>
      {selectedVideoId && (
        <div style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <QoEOverviewCards data={videoQoe} loading={loading.videoQoe} />
            </Col>
            <Col xs={24} lg={12}>
              <VideoEngagementCards data={videoEngagement} loading={loading.videoEngagement} />
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

// --- Sub-components ---

const QoETimeseriesTable = ({ data, loading }) => {
  if (loading) {
    return (
      <Card title="Evolucao Temporal QoE" size="small">
        <div style={{ textAlign: "center", padding: 20 }}><Spin /></div>
      </Card>
    );
  }
  if (!data?.data || data.data.length === 0) {
    return (
      <Card title="Evolucao Temporal QoE" size="small">
        <Empty description="Sem dados para o periodo" />
      </Card>
    );
  }

  const maxValue = Math.max(...data.data.map((d) => d.value || 0));

  const renderBar = (value, color) => {
    const width = maxValue > 0 ? (value / maxValue) * 100 : 0;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            width: `${Math.max(width, 2)}%`,
            minWidth: value > 0 ? "4px" : "0",
            maxWidth: "100%",
            height: "14px",
            backgroundColor: color,
            borderRadius: "2px",
            transition: "width 0.3s ease",
          }}
        />
        <span style={{ fontSize: 12, color: "#666", minWidth: 50 }}>
          {typeof value === "number" ? value.toFixed(3) : value}
        </span>
      </div>
    );
  };

  const columns = [
    {
      title: "Data",
      dataIndex: "timestamp",
      key: "timestamp",
      width: 120,
      fixed: "left",
      render: (val) => val ? new Date(val).toLocaleDateString("pt-BR") : "-",
    },
    {
      title: <Tag color="blue">Valor</Tag>,
      dataIndex: "value",
      key: "value",
      render: (val) => renderBar(val || 0, "#1890ff"),
    },
    {
      title: "Views",
      dataIndex: "views",
      key: "views",
      width: 120,
      render: (val) => val != null ? val.toLocaleString() : "-",
    },
  ];

  return (
    <Card title="Evolucao Temporal QoE" size="small">
      <Table
        columns={columns}
        dataSource={data.data}
        rowKey={(record, idx) => record.timestamp || idx}
        pagination={false}
        size="small"
        scroll={{ x: 500, y: 300 }}
      />
    </Card>
  );
};

const TrailEngagementCards = ({ data, loading }) => {
  if (loading) {
    return <Card size="small"><div style={{ textAlign: "center", padding: 20 }}><Spin /></div></Card>;
  }
  if (!data) return <Card size="small"><Empty description="Sem dados" /></Card>;

  const formatTime = (seconds) => {
    if (!seconds) return "0h";
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours.toLocaleString()}h ${mins}m`;
    return `${mins}m`;
  };

  return (
    <Card
      title={
        <span>
          <FundOutlined style={{ color: "#1890ff", marginRight: 8 }} />
          Engagement: {data.trailName || "Trilha"}
        </span>
      }
      size="small"
    >
      <Row gutter={[8, 12]}>
        <Col span={8}>
          <Statistic
            title={<span style={{ fontSize: 11 }}><PlayCircleOutlined style={{ marginRight: 4 }} />Sessions</span>}
            value={data.totalSessions || 0}
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title={<span style={{ fontSize: 11 }}><CheckCircleOutlined style={{ marginRight: 4 }} />Concluidas</span>}
            value={data.completedSessions || 0}
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title={<span style={{ fontSize: 11 }}><FundOutlined style={{ marginRight: 4 }} />Conclusao</span>}
            value={data.completionRate?.toFixed(1) || 0}
            suffix="%"
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title={<span style={{ fontSize: 11 }}><FundOutlined style={{ marginRight: 4 }} />Progresso</span>}
            value={data.avgProgressPercent?.toFixed(1) || 0}
            suffix="%"
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title={<span style={{ fontSize: 11 }}><UserOutlined style={{ marginRight: 4 }} />Alunos</span>}
            value={data.uniqueStudents || 0}
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title={<span style={{ fontSize: 11 }}><ClockCircleOutlined style={{ marginRight: 4 }} />Watch Time</span>}
            value={formatTime(data.totalWatchTimeSeconds)}
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
      </Row>
    </Card>
  );
};

const TrailFunnelTable = ({ data, loading }) => {
  if (loading) {
    return (
      <Card title="Funnel por Modulo" size="small">
        <div style={{ textAlign: "center", padding: 20 }}><Spin /></div>
      </Card>
    );
  }
  if (!data?.modules || data.modules.length === 0) {
    return (
      <Card title="Funnel por Modulo" size="small">
        <Empty description="Sem dados" />
      </Card>
    );
  }

  const columns = [
    { title: "#", dataIndex: "position", key: "position", width: 50 },
    { title: "Modulo", dataIndex: "moduleName", key: "moduleName", ellipsis: true },
    {
      title: "Iniciaram",
      dataIndex: "studentsStarted",
      key: "studentsStarted",
      width: 100,
      render: (val) => (val || 0).toLocaleString(),
    },
    {
      title: "Concluiram",
      dataIndex: "studentsFinished",
      key: "studentsFinished",
      width: 100,
      render: (val) => (val || 0).toLocaleString(),
    },
    {
      title: "Conclusao",
      dataIndex: "completionRate",
      key: "completionRate",
      width: 120,
      render: (val) => (
        <Progress
          percent={val || 0}
          size="small"
          strokeColor={val >= 70 ? "#52c41a" : val >= 40 ? "#fa8c16" : "#ff4d4f"}
        />
      ),
    },
    {
      title: "Drop-off",
      dataIndex: "dropOffRate",
      key: "dropOffRate",
      width: 90,
      render: (val) => {
        const color = val > 30 ? "red" : val > 15 ? "orange" : "green";
        return <Tag color={color}>{val?.toFixed(1)}%</Tag>;
      },
    },
  ];

  return (
    <Card
      title={
        <span>
          <FallOutlined style={{ color: "#fa8c16", marginRight: 8 }} />
          Funnel: {data.trailName || "Trilha"}
        </span>
      }
      size="small"
    >
      <Table
        columns={columns}
        dataSource={data.modules}
        rowKey="moduleId"
        pagination={false}
        size="small"
        scroll={{ x: 600 }}
      />
    </Card>
  );
};

const ModuleDropOffTable = ({ data, loading }) => {
  if (loading) {
    return (
      <Card title="Drop-off por Video" size="small">
        <div style={{ textAlign: "center", padding: 20 }}><Spin /></div>
      </Card>
    );
  }
  if (!data?.videos || data.videos.length === 0) {
    return (
      <Card title="Drop-off por Video" size="small">
        <Empty description="Sem dados" />
      </Card>
    );
  }

  const columns = [
    { title: "#", dataIndex: "position", key: "position", width: 50 },
    { title: "Video", dataIndex: "videoName", key: "videoName", ellipsis: true },
    {
      title: "Iniciaram",
      dataIndex: "viewsStarted",
      key: "viewsStarted",
      width: 100,
    },
    {
      title: "Concluiram",
      dataIndex: "viewsCompleted",
      key: "viewsCompleted",
      width: 100,
    },
    {
      title: "Conclusao",
      dataIndex: "completionRate",
      key: "completionRate",
      width: 120,
      render: (val) => (
        <Progress
          percent={val || 0}
          size="small"
          strokeColor={val >= 70 ? "#52c41a" : val >= 40 ? "#fa8c16" : "#ff4d4f"}
        />
      ),
    },
    {
      title: "Progresso",
      dataIndex: "avgProgress",
      key: "avgProgress",
      width: 100,
      render: (val) => `${val?.toFixed(1) || 0}%`,
    },
    {
      title: "QoE",
      dataIndex: "avgQoEScore",
      key: "avgQoEScore",
      width: 80,
      render: (val) => {
        const pct = val ? val * 100 : 0;
        const color = pct >= 80 ? "green" : pct >= 50 ? "orange" : "red";
        return <Tag color={color}>{pct.toFixed(1)}%</Tag>;
      },
    },
  ];

  return (
    <Card
      title={
        <span>
          <FallOutlined style={{ color: "#eb2f96", marginRight: 8 }} />
          Drop-off: {data.moduleName || "Modulo"}
        </span>
      }
      size="small"
    >
      <Table
        columns={columns}
        dataSource={data.videos}
        rowKey="videoId"
        pagination={false}
        size="small"
        scroll={{ x: 600 }}
      />
    </Card>
  );
};

const VideoEngagementCards = ({ data, loading }) => {
  if (loading) {
    return <Card size="small"><div style={{ textAlign: "center", padding: 20 }}><Spin /></div></Card>;
  }
  if (!data) return <Card size="small"><Empty description="Sem dados" /></Card>;

  const formatTime = (seconds) => {
    if (!seconds) return "0m";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  return (
    <Card
      title={
        <span>
          <DashboardOutlined style={{ color: "#722ed1", marginRight: 8 }} />
          Engagement: {data.videoName || "Video"}
        </span>
      }
      size="small"
    >
      <Row gutter={[8, 12]}>
        <Col span={8}>
          <Statistic
            title={<span style={{ fontSize: 11 }}>Sessions</span>}
            value={data.totalSessions || 0}
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title={<span style={{ fontSize: 11 }}>Concluidas</span>}
            value={data.completedSessions || 0}
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title={<span style={{ fontSize: 11 }}>Conclusao</span>}
            value={data.completionRate?.toFixed(1) || 0}
            suffix="%"
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title={<span style={{ fontSize: 11 }}>Progresso</span>}
            value={data.avgProgressPercent?.toFixed(1) || 0}
            suffix="%"
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title={<span style={{ fontSize: 11 }}>Watch Time</span>}
            value={formatTime(data.avgWatchTimeSeconds)}
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title={<span style={{ fontSize: 11 }}>Viewers</span>}
            value={data.uniqueViewers || 0}
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
      </Row>
    </Card>
  );
};

export default QoESection;
